import React, { useEffect, useState } from 'react';
import {  Modal, Panel } from 'react-bootstrap';
import Select from 'react-select';

import '../../Assets/Ui/RequestCounter.css';
import '../../Assets/Ui/RequestAnalytics.css';
import { fetchApi } from '../../Network/NetworkService';
import isoDate from '../../Services/toIsoDate.js';
import { notif } from '../Ui/Notification';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import { comparisonArr, priorityStates, rqState, typeStates } from '../../Constants.jsx';


const clientTypeStates = [
    { value: 'all', label: 'Tous les clients' },
    { value: 'new', label: 'Nouveau client' },
    { value: 'old', label: 'Récurrent' },
];

const abVariantStates = [
    { value: 'all', label: 'Tous' },
    { value: 'v1', label: 'V1' },
    { value: 'v2', label: 'V2' },
];

const sortingObject = (obj) => {
    const sortable = Object.entries(obj)
        .sort(([, a], [, b]) => b-a)
        .reduce((r, [k, v]) => ({ ...r, [k]: v }), {});

    return sortable;
}

// Shared styles for react-select with proper z-index
const selectStyles = {
    option: (s) => ({ ...s, color: 'black' }),
    menu: (s) => ({ ...s, zIndex: 9999 }),
    menuPortal: (s) => ({ ...s, zIndex: 9999 })
};

const RequestAnalytics = (props) => {
    const [total, setTotal] = useState(0);
    const [state, setState] = useState([]);
    const [typeState, setTypeState] = useState([]);
    const [motifs, setMotifs] = useState([]);
    const [showMore, setShowMore] = useState(false);
    const [comparison, setComparison] = useState( {value: 'yesterday', label: "Jour précédent"} );
    const [range, setRange] = useState([new Date(), new Date()]);
    const [rangeCompare, setRangeCompare] = useState([new Date(), new Date()]);
    const [priorityState, setPriorityState] = useState(null);
    const [clientFilter, setClientFilter] = useState({ value: 'all', label: 'Tous les clients' });
    const [abVariant, setAbVariant] = useState({ value: 'all', label: 'Tous' });
    const [loading, setLoading] = useState(false);

    // Aggregated data from new endpoint
    const [analyticsData, setAnalyticsData] = useState(null);
    const [analyticsDataCompare, setAnalyticsDataCompare] = useState(null);
    const [adCosts, setAdCosts] = useState({});
    const [adCostsCompare, setAdCostsCompare] = useState({});

    const getCount = () => {
        setTotal(-1);
        setLoading(true);
        setAdCosts({});
        setAdCostsCompare({});

        let startDate = range[0];
        let endDate= range[1];
        let startDateCompare, endDateCompare;

        if (comparison && comparison.value === 'yesterday') {
            startDateCompare = new Date (new Date(range[0]).setDate(new Date(range[0]).getDate() - 1));
            endDateCompare = new Date (new Date(range[1]).setDate(new Date(range[1]).getDate() -1));
        };
        if (comparison && comparison.value === 'last-month') {
            startDateCompare = new Date (new Date(range[0]).setDate(new Date(range[0]).getDate() -30));
            endDateCompare = new Date (new Date(range[1]).setDate(new Date(range[1]).getDate() -30));
        };
        if (comparison && comparison.value === 'last-year') {
            startDateCompare = new Date (new Date(range[0]).setDate(new Date(range[0]).getDate() - 365));
            endDateCompare = new Date (new Date(range[1]).setDate(new Date(range[1]).getDate() - 365));
        };
        if (comparison && comparison.value === 'personalized') {
            startDateCompare = rangeCompare[0];
            endDateCompare = rangeCompare[1];
        }

        const buildQueryString = (start, end) => {
            let query = `?start=${isoDate(start, 0, 0)}&end=${isoDate(end, 23, 59, 59)}`;
            if (state.length > 0) query += `&state=${state.map((v) => v.value).toString()}`;
            if (typeState.length > 0) query += `&typeState=${typeState.map((v) => v.value).toString()}`;
            if (priorityState) query += `&priorityState=${priorityState.value}`;
            if (abVariant && abVariant.value !== 'all') query += `&abVariant=${abVariant.value}`;
            return query;
        };

        const fetchAdCosts = (start, end) => fetchApi(`/analyticData/getAdCosts`, {
            method: 'POST',
            body: JSON.stringify({ dateValue: [start, end] })
        }).catch(() => ({ data: {} }));

        const apis = [
            fetchApi(`/requests/analytics/aggregated${buildQueryString(startDate, endDate)}`),
            fetchApi(`/requests/analytics/aggregated${buildQueryString(startDateCompare, endDateCompare)}`),
            fetchAdCosts(startDate, endDate),
            fetchAdCosts(startDateCompare, endDateCompare)
        ];

        Promise.all(apis)
            .then(responses => {
                setAnalyticsData(responses[0].data);
                setAnalyticsDataCompare(responses[1].data);
                setAdCosts(responses[2].data || {});
                setAdCostsCompare(responses[3].data || {});
                setTotal(responses[0].data.total);
                setLoading(false);
            })
            .catch((err) => {
                setLoading(false);
                notif.addNotification(
                    'Erreur',
                    'Erreur de recuperation des donnees: ' + err,
                    'warning'
                )
            });
    };

    const getMotifGeneraliste = () => {
        setMotifs([]);
        fetchApi(`/requests/analytics/specificPathology/aggregated?start=${isoDate(range[0], 0, 0)}&end=${isoDate(range[1], 23, 59, 59)}${state.length > 0 ? `&state=${state.map((v) => v.value).toString()}` : ''}&typeState=generaliste`)
            .then(res => {
                // Transform aggregated motifs into display format (rows of 3)
                const motifsArr = res.motifs.map(m => `${m.motif} (${m.count})`);
                setMotifs(motifsArr.reduce((acc, x, i) => {
                    const idx = Math.floor(i / 3);
                    acc[idx] = [...(acc[idx] || []), x];
                    return acc;
                  }, []));
            })
            .catch((err) => {
                notif.addNotification(
                    'Erreur',
                    'Erreur de recuperation des donnees: ' + err,
                    'warning'
                )
            });
    };

    // Helper functions to get data from aggregated response
    const getSourceCount = (source, data) => {
        if (!data || !data.bySource) return 0;
        return data.bySource[source] ? data.bySource[source].count : 0;
    };

    const getSourceRevenue = (source, data) => {
        if (!data || !data.bySource) return 0;
        return data.bySource[source] ? data.bySource[source].revenue : 0;
    };

    const getSourceDoctorFee = (source, data) => {
        if (!data || !data.bySource) return 0;
        return data.bySource[source] ? data.bySource[source].doctorFee : 0;
    };

    const getSourcePriorityCount = (source, data) => {
        if (!data || !data.bySource) return 0;
        return data.bySource[source] ? data.bySource[source].priorityCount : 0;
    };

    const getSourceNonPriorityCount = (source, data) => {
        if (!data || !data.bySource) return 0;
        return data.bySource[source] ? data.bySource[source].nonPriorityCount : 0;
    };

    const costPerConsultation = (source) => {
        if (source === "google" || source === "bing" || source === "naturel") return 0;
        if (source === "gescall" || source === "medifil" || source === "cortel" || source === "pharmacy") return 10;
        if (source === "skycall" || source === "skycall-voice" || source === "skycall-api") return 15;
        return 0;
    };

    const getDisplayedTotal = () => {
        if (!analyticsData) return 0;
        if (clientFilter.value === 'new') return analyticsData.clientType.newClientCount;
        if (clientFilter.value === 'old') return analyticsData.clientType.oldClientCount;
        return analyticsData.total;
    };

    const getDisplayedTotalCompare = () => {
        if (!analyticsDataCompare) return 0;
        if (clientFilter.value === 'new') return analyticsDataCompare.clientType.newClientCount;
        if (clientFilter.value === 'old') return analyticsDataCompare.clientType.oldClientCount;
        return analyticsDataCompare.total;
    };

    const getDisplayedRevenue = () => {
        if (!analyticsData) return 0;
        if (clientFilter.value === 'new') return analyticsData.clientType.newClientRevenue;
        if (clientFilter.value === 'old') return analyticsData.clientType.oldClientRevenue;
        return analyticsData.totals.totalRevenue;
    };

    const getDisplayedSourceCount = (source) => {
        if (!analyticsData) return 0;
        if (clientFilter.value === 'new') {
            return analyticsData.newClientsBySource[source] ? analyticsData.newClientsBySource[source].count : 0;
        }
        if (clientFilter.value === 'old') {
            return analyticsData.oldClientsBySource[source] ? analyticsData.oldClientsBySource[source].count : 0;
        }
        return getSourceCount(source, analyticsData);
    };

    const getDisplayedSourceCountCompare = (source) => {
        if (!analyticsDataCompare) return 0;
        if (clientFilter.value === 'new') {
            return analyticsDataCompare.newClientsBySource[source] ? analyticsDataCompare.newClientsBySource[source].count : 0;
        }
        if (clientFilter.value === 'old') {
            return analyticsDataCompare.oldClientsBySource[source] ? analyticsDataCompare.oldClientsBySource[source].count : 0;
        }
        return getSourceCount(source, analyticsDataCompare);
    };

    const getDisplayedSourceRevenue = (source) => {
        if (!analyticsData) return 0;
        if (clientFilter.value === 'new') {
            return analyticsData.newClientsBySource[source] ? analyticsData.newClientsBySource[source].revenue : 0;
        }
        if (clientFilter.value === 'old') {
            return analyticsData.oldClientsBySource[source] ? analyticsData.oldClientsBySource[source].revenue : 0;
        }
        return getSourceRevenue(source, analyticsData);
    };

    const getDisplayedDoctorFee = () => {
        if (!analyticsData) return 0;
        if (clientFilter.value === 'new') return analyticsData.clientType.newClientDoctorFee || 0;
        if (clientFilter.value === 'old') return analyticsData.clientType.oldClientDoctorFee || 0;
        return analyticsData.totals.totalDoctorFee;
    };

    const getDisplayedSourceDoctorFee = (source) => {
        if (!analyticsData) return 0;
        if (clientFilter.value === 'new') {
            return analyticsData.newClientsBySource[source] ? (analyticsData.newClientsBySource[source].doctorFee || 0) : 0;
        }
        if (clientFilter.value === 'old') {
            return analyticsData.oldClientsBySource[source] ? (analyticsData.oldClientsBySource[source].doctorFee || 0) : 0;
        }
        return getSourceDoctorFee(source, analyticsData);
    };

    // Option C: Google Ads spend is an acquisition cost for NEW clients only
    // (returning clients come back via direct/email/SMS, not paid search).
    const getAdCostForSource = (source, costs) => {
        if (clientFilter.value === 'old') return 0;
        return (costs && costs[source]) ? costs[source] : 0;
    };

    const getDisplayedSourceCost = (source) => {
        if (!analyticsData) return 0;
        if (source === 'google') return getAdCostForSource(source, adCosts);
        return getDisplayedSourceCount(source) * costPerConsultation(source);
    };

    const getDisplayedTotalAcquisitionCost = () => {
        if (!analyticsData || !analyticsData.bySource) return 0;
        return Object.keys(analyticsData.bySource).reduce((sum, source) => sum + getDisplayedSourceCost(source), 0);
    };

    const getDisplayedSourceRevenueCompare = (source) => {
        if (!analyticsDataCompare) return 0;
        if (clientFilter.value === 'new') {
            return analyticsDataCompare.newClientsBySource[source] ? analyticsDataCompare.newClientsBySource[source].revenue : 0;
        }
        if (clientFilter.value === 'old') {
            return analyticsDataCompare.oldClientsBySource[source] ? analyticsDataCompare.oldClientsBySource[source].revenue : 0;
        }
        return getSourceRevenue(source, analyticsDataCompare);
    };

    const getDisplayedSourceDoctorFeeCompare = (source) => {
        if (!analyticsDataCompare) return 0;
        if (clientFilter.value === 'new') {
            return analyticsDataCompare.newClientsBySource[source] ? (analyticsDataCompare.newClientsBySource[source].doctorFee || 0) : 0;
        }
        if (clientFilter.value === 'old') {
            return analyticsDataCompare.oldClientsBySource[source] ? (analyticsDataCompare.oldClientsBySource[source].doctorFee || 0) : 0;
        }
        return getSourceDoctorFee(source, analyticsDataCompare);
    };

    const getDisplayedSourceCostCompare = (source) => {
        if (!analyticsDataCompare) return 0;
        if (source === 'google') return getAdCostForSource(source, adCostsCompare);
        return getDisplayedSourceCountCompare(source) * costPerConsultation(source);
    };

    const getStateCount = (stateKey, data) => {
        if (!data || !data.byState) return 0;
        return data.byState[stateKey] || 0;
    };

    const getPriorityCount = (data) => {
        if (!data || !data.priority) return 0;
        return data.priority.priorityCount || 0;
    };

    const getNonPriorityCount = (data) => {
        if (!data || !data.priority) return 0;
        return data.priority.nonPriorityCount || 0;
    };

    useEffect(() => {
        getCount();
        getMotifGeneraliste();
    }, []);

    const getIcon = (data, dataCompare) => {
        if (data > dataCompare) {
            return '↗️';
        } else if (data < dataCompare) {
            return '↘️';
        } else {
            return '';
        }
    }

    const getPathology = () => {
        if (!analyticsData || !analyticsData.byType) return {};
        return sortingObject(analyticsData.byType);
    }

    const getPathologyCompare = () => {
        if (!analyticsDataCompare || !analyticsDataCompare.byType) return {};
        const sorted = getPathology();
        const result = {};
        Object.keys(sorted).forEach(path => {
            result[path] = analyticsDataCompare.byType[path] || 0;
        });
        return result;
    }

    const getDoctor = () => {
        if (!analyticsData || !analyticsData.byDoctor) return {};
        const doctorObj = {};
        Object.keys(analyticsData.byDoctor).forEach(name => {
            doctorObj[name] = analyticsData.byDoctor[name].count;
        });
        return sortingObject(doctorObj);
    }

    const getDoctorCompare = () => {
        if (!analyticsDataCompare || !analyticsDataCompare.byDoctor) return {};
        const sorted = getDoctor();
        const result = {};
        Object.keys(sorted).forEach(doc => {
            result[doc] = analyticsDataCompare.byDoctor[doc] ? analyticsDataCompare.byDoctor[doc].count : 0;
        });
        return result;
    }

    const getStepObj = () => {
        if (!analyticsData || !analyticsData.byIncompleteStep) return {};
        return analyticsData.byIncompleteStep;
    }

    const getActiveSources = () => {
        if (!analyticsData || !analyticsData.bySource) return [];
        // Get all sources dynamically from the data, sorted by count descending
        return Object.keys(analyticsData.bySource)
            .filter(source => getDisplayedSourceCount(source) > 0)
            .sort((a, b) => getDisplayedSourceCount(b) - getDisplayedSourceCount(a));
    };

    return (
        <div className='r-analytics'>
            <Modal
                show={loading}
                backdrop="static"
                keyboard={false}
                size="lg"
                className="modal-spinner"
            >
                <span className="loader" />
                {loading && <p>Récupération des données en cours...</p>}
            </Modal>
        <Panel>
            <Panel.Heading className='heading'>
                <h2>Analyses des requêtes</h2>
                <div>
                    <h6>Compteur du {' '} </h6>
                    <DateRangePicker onChange={value => setRange(value)} value={range} maxDate={new Date()}/>

                    <h6>{' '} Par rapport {' '}</h6>
                    <Select
                        value={comparison}
                        onChange={(v) => setComparison(v)}
                        options={comparisonArr}
                        styles={selectStyles}
                        role="checkbox"
                        className='filter-status'
                    />
                    {
                        comparison.value === 'personalized' &&
                        <DateRangePicker onChange={value => setRangeCompare(value)} value={rangeCompare} maxDate={new Date()}/>
                    }
                </div>
                <div>
                    <Select
                        value={clientFilter}
                        onChange={(v) => setClientFilter(v)}
                        options={clientTypeStates}
                        styles={selectStyles}
                        role="checkbox"
                        placeholder="Filtre par type de client ..."
                        className='filter-status'
                    />
                    <Select
                        value={state}
                        onChange={(v) => setState(v)}
                        options={rqState}
                        isMulti
                        styles={selectStyles}
                        role="checkbox"
                        placeholder="Filtre par statut ..."
                        className='filter-status'
                    />
                    <Select
                        value={typeState}
                        onChange={(v) => setTypeState(v)}
                        options={typeStates}
                        isMulti
                        styles={selectStyles}
                        role="checkbox"
                        placeholder="Filtre par pathologie ..."
                        className='filter-status'
                    />
                    <Select
                        value={priorityState}
                        onChange={(v) => {
                           setPriorityState(v)
                        }}
                        options={priorityStates}
                        styles={selectStyles}
                        role="checkbox"
                        placeholder="Filtre consultation urgente ..."
                        className='filter-status'
                    />
                    <Select
                        value={abVariant}
                        onChange={(v) => setAbVariant(v)}
                        options={abVariantStates}
                        styles={selectStyles}
                        role="checkbox"
                        placeholder="A/B Variant ..."
                        className='filter-status'
                    />
                    <button className='submit-button' onClick={() => {
                        getCount();
                        getMotifGeneraliste();
                    }}>Chercher</button>
                </div>
            </Panel.Heading>
            <Panel.Body>
            <div className='source-table'>
                    <h1>Par priorité</h1>
                    <table className="table table-hover mb-0 analytic-table">
                        <thead>
                            <tr>
                                <th rowSpan={2}>Source</th>
                                <th rowSpan={2}>Nombre total de requêtes</th>
                                <th colSpan={2}>Urgente</th>
                                <th colSpan={2}>Non-urgentes</th>
                            </tr>
                            <tr>
                                <th>Nombre</th>
                                <th>Taux</th>
                                <th>Nombre</th>
                                <th>Taux</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className='data'>
                                <td>Total</td>
                                <td>{getDisplayedTotal()} {getIcon(getDisplayedTotal(), getDisplayedTotalCompare())}</td>
                                <td>{getPriorityCount(analyticsData)} {getIcon(getPriorityCount(analyticsData), getPriorityCount(analyticsDataCompare))}</td>
                                <td>100%</td>
                                <td>{getNonPriorityCount(analyticsData)} {getIcon(getNonPriorityCount(analyticsData), getNonPriorityCount(analyticsDataCompare))}</td>
                                <th>100%</th>
                            </tr>
                            {
                                getActiveSources().map(source => <tr className='data' key={source}>
                                    <td>{source}</td>
                                    <td>{getDisplayedSourceCount(source)} {getIcon(getDisplayedSourceCount(source), getDisplayedSourceCountCompare(source))}</td>
                                    <td>{getSourcePriorityCount(source, analyticsData)} {getIcon(getSourcePriorityCount(source, analyticsData), getSourcePriorityCount(source, analyticsDataCompare))}</td>
                                    <td>{getPriorityCount(analyticsData) > 0 ? ((getSourcePriorityCount(source, analyticsData)/getPriorityCount(analyticsData)) *100).toFixed(2) : 0} %</td>
                                    <td>{getSourceNonPriorityCount(source, analyticsData)} {getIcon(getSourceNonPriorityCount(source, analyticsData), getSourceNonPriorityCount(source, analyticsDataCompare))}</td>
                                    <td>{getNonPriorityCount(analyticsData) > 0 ? ((getSourceNonPriorityCount(source, analyticsData)/getNonPriorityCount(analyticsData)) *100).toFixed(2) : 0} %</td>

                                </tr>)
                            }
                            <tr className='compare'>

                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className='source-table'>
                    <h1>Par Source</h1>
                    <table className="table table-hover mb-0 analytic-table">
                        <thead>
                            <tr>
                                <th>Source</th>
                                <th>Nombre</th>
                                <th>Taux</th>
                                <th>CA</th>
                                <th>Honoraires des médecins</th>
                                <th>Revenu brut</th>
                                <th>Cout d'acquisition</th>
                                <th>Revenu</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(() => {
                                const totalDoctorFee = getDisplayedDoctorFee();
                                const totalCA = getDisplayedRevenue();
                                const totalCost = getDisplayedTotalAcquisitionCost();
                                return (
                                    <tr>
                                        <td>Total</td>
                                        <td>{getDisplayedTotal()} {getIcon(getDisplayedTotal(), getDisplayedTotalCompare())}</td>
                                        <td>100%</td>
                                        <td>{totalCA}</td>
                                        <td>{totalDoctorFee}</td>
                                        <td>{analyticsData ? totalCA - totalDoctorFee : 0}</td>
                                        <td>{totalCost}</td>
                                        <td>{analyticsData ? totalCA - totalDoctorFee - totalCost : 0}</td>
                                    </tr>
                                );
                            })()}
                            {
                                getActiveSources().map((source, i) => {
                                    const ca = getDisplayedSourceRevenue(source);
                                    const docFee = getDisplayedSourceDoctorFee(source);
                                    const cost = getDisplayedSourceCost(source);
                                    const caCompare = getDisplayedSourceRevenueCompare(source);
                                    const docFeeCompare = getDisplayedSourceDoctorFeeCompare(source);
                                    const costCompare = getDisplayedSourceCostCompare(source);
                                    return [
                                        <tr key={i}>
                                            <td rowSpan={2}>{source}</td>
                                            <td>{getDisplayedSourceCount(source)} {getIcon(getDisplayedSourceCount(source), getDisplayedSourceCountCompare(source))}</td>
                                            <td>{getDisplayedTotal() > 0 ? `${(getDisplayedSourceCount(source)/getDisplayedTotal() * 100 ).toFixed(2)}%` : '0%'}</td>
                                            <td>{ca}</td>
                                            <td>{docFee}</td>
                                            <td>{ca - docFee}</td>
                                            <td>{cost}</td>
                                            <td>{ca - docFee - cost}</td>
                                        </tr>,
                                        <tr key={`${i}-compare`} className='compare'>
                                            <td>{getDisplayedSourceCountCompare(source)}</td>
                                            <td>{getDisplayedTotalCompare() > 0 ? `${(getDisplayedSourceCountCompare(source)/getDisplayedTotalCompare() * 100 ).toFixed(2)}%` : '0%'}</td>
                                            <td>{caCompare}</td>
                                            <td>{docFeeCompare}</td>
                                            <td>{caCompare - docFeeCompare}</td>
                                            <td>{costCompare}</td>
                                            <td>{caCompare - docFeeCompare - costCompare}</td>
                                        </tr>
                                    ]

                                })
                            }
                        </tbody>
                    </table>
                    <p>*Zone gris: Donnés du {comparison.label}</p>

                </div>

                <div className='source-table'>
                    <h1>Par Client</h1>
                    <table className="table table-hover mb-0 analytic-table">
                        <thead>
                            <tr>
                                <th colSpan={2}>Source</th>
                                <th>Nombre</th>
                                <th>Taux</th>
                                <th>CA</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td rowSpan={2}>Total</td>
                                <td>Nouveau</td>
                                <td>{analyticsData ? analyticsData.clientType.newClientCount : 0} {getIcon(analyticsData ? analyticsData.clientType.newClientCount : 0, analyticsDataCompare ? analyticsDataCompare.clientType.newClientCount : 0)}</td>
                                <td>{total > 0 ? `${(analyticsData.clientType.newClientCount/total * 100 ).toFixed(2)}%` : '0%'}</td>
                                <td>{analyticsData ? analyticsData.clientType.newClientRevenue.toLocaleString() : 0}</td>

                            </tr>
                            <tr>
                                <td>Récurrent</td>
                                <td>{analyticsData ? analyticsData.clientType.oldClientCount : 0} {getIcon(analyticsData ? analyticsData.clientType.oldClientCount : 0, analyticsDataCompare ? analyticsDataCompare.clientType.oldClientCount : 0)}</td>
                                <td>{total > 0 ? `${(analyticsData.clientType.oldClientCount/total * 100 ).toFixed(2)}%` : '0%'}</td>
                                <td>{analyticsData ? analyticsData.clientType.oldClientRevenue.toLocaleString() : 0}</td>

                            </tr>

                            {
                                getActiveSources().map((source, i) => {
                                    const newCount = analyticsData && analyticsData.newClientsBySource[source] ? analyticsData.newClientsBySource[source].count : 0;
                                    const oldCount = analyticsData && analyticsData.oldClientsBySource[source] ? analyticsData.oldClientsBySource[source].count : 0;
                                    const newCountCompare = analyticsDataCompare && analyticsDataCompare.newClientsBySource[source] ? analyticsDataCompare.newClientsBySource[source].count : 0;
                                    const oldCountCompare = analyticsDataCompare && analyticsDataCompare.oldClientsBySource[source] ? analyticsDataCompare.oldClientsBySource[source].count : 0;
                                    const newRevenue = analyticsData && analyticsData.newClientsBySource[source] ? analyticsData.newClientsBySource[source].revenue : 0;
                                    const oldRevenue = analyticsData && analyticsData.oldClientsBySource[source] ? analyticsData.oldClientsBySource[source].revenue : 0;
                                    const totalNew = analyticsData ? analyticsData.clientType.newClientCount : 0;
                                    const totalOld = analyticsData ? analyticsData.clientType.oldClientCount : 0;

                                    if (clientFilter.value === "new") {
                                        return (
                                            <tr key={i}>
                                               <td rowSpan={1}>{source}</td>
                                               <td>Nouveau</td>
                                               <td>{newCount} {getIcon(newCount, newCountCompare)}</td>
                                               <td>{totalNew > 0 ? `${(newCount/totalNew * 100 ).toFixed(2)}%` : '0%'}</td>
                                               <td>{newRevenue}</td>
                                            </tr>)
                                    } else if (clientFilter.value === "old") {
                                        return (
                                            <tr key={`${i}-compare`}>
                                                <td rowSpan={1}>{source}</td>
                                                <td>Récurrent</td>
                                                <td>{oldCount} {getIcon(oldCount, oldCountCompare)}</td>
                                                <td>{totalOld > 0 ? `${(oldCount/totalOld * 100 ).toFixed(2)}%` : '0%'}</td>
                                                <td>{oldRevenue}</td>
                                            </tr>
                                        )
                                    } else {
                                        return [
                                            <tr key={i}>
                                                <td rowSpan={2}>{source}</td>
                                                <td>Nouveau</td>
                                                <td>{newCount} {getIcon(newCount, newCountCompare)}</td>
                                                <td>{totalNew > 0 ? `${(newCount/totalNew * 100 ).toFixed(2)}%` : '0%'}</td>
                                                <td>{newRevenue}</td>

                                            </tr>,
                                            <tr key={`${i}-compare`}>
                                                <td>Récurrent</td>
                                                <td>{oldCount} {getIcon(oldCount, oldCountCompare)}</td>
                                                <td>{totalOld > 0 ? `${(oldCount/totalOld * 100 ).toFixed(2)}%` : '0%'}</td>
                                                <td>{oldRevenue}</td>

                                            </tr>
                                        ]
                                    }
                                })
                            }
                        </tbody>
                    </table>
                    <p>*Nouveau: Nombre de consultation réalisé par nouveau client</p>
                    <p>*Récurrent: Nombre de consultation réalisé par ancien client</p>
                </div>

                <div className='source-table'>
                    <h1>Par status</h1>
                    <table className="table table-hover mb-0 analytic-table">
                        <thead>
                            <tr>
                                <th colSpan={2}>Incomplète</th>
                                <th colSpan={2}>En attente</th>
                                <th colSpan={2}>Validé</th>
                                <th colSpan={2}>Remboursé</th>
                                <th colSpan={2}>Annulé</th>
                                <th colSpan={2}>Annulé par médecin</th>
                                <th colSpan={2}>À appeler</th>
                                <th colSpan={2}>Non à distance</th>
                            </tr>
                            <tr>
                                <th>Nombre</th>
                                <th>Taux</th>
                                <th>Nombre</th>
                                <th>Taux</th>
                                <th>Nombre</th>
                                <th>Taux</th>
                                <th>Nombre</th>
                                <th>Taux</th>
                                <th>Nombre</th>
                                <th>Taux</th>
                                <th>Nombre</th>
                                <th>Taux</th>
                                <th>Nombre</th>
                                <th>Taux</th>
                                <th>Nombre</th>
                                <th>Taux</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className='data'>
                               <td>{getStateCount('incomplete', analyticsData)} {getIcon(getStateCount('incomplete', analyticsData), getStateCount('incomplete', analyticsDataCompare))}</td>
                               <td>{getDisplayedTotal() > 0  ? `${(getStateCount('incomplete', analyticsData)/getDisplayedTotal() * 100 ).toFixed(2)}%` : '0%'}</td>
                               <td>{getStateCount('waiting', analyticsData)} {getIcon(getStateCount('waiting', analyticsData), getStateCount('waiting', analyticsDataCompare))}</td>
                               <td>{getDisplayedTotal() > 0  ? `${(getStateCount('waiting', analyticsData)/getDisplayedTotal() * 100 ).toFixed(2)}%` : '0%'}</td>
                               <td>{getStateCount('valide', analyticsData)} {getIcon(getStateCount('valide', analyticsData), getStateCount('valide', analyticsDataCompare))}</td>
                               <td>{getDisplayedTotal() > 0  ? `${(getStateCount('valide', analyticsData)/getDisplayedTotal() * 100 ).toFixed(2)}%` : '0%'}</td>
                               <td>{getStateCount('refunded', analyticsData)} {getIcon(getStateCount('refunded', analyticsData), getStateCount('refunded', analyticsDataCompare))}</td>
                               <td>{getDisplayedTotal() > 0  ? `${(getStateCount('refunded', analyticsData)/getDisplayedTotal() * 100 ).toFixed(2)}%` : '0%'}</td>
                               <td>{getStateCount('canceled', analyticsData)} {getIcon(getStateCount('canceled', analyticsData), getStateCount('canceled', analyticsDataCompare))}</td>
                               <td>{getDisplayedTotal() > 0  ? `${(getStateCount('canceled', analyticsData)/getDisplayedTotal() * 100 ).toFixed(2)}%` : '0%'}</td>
                               <td>{getStateCount('canceledByDoctor', analyticsData)} {getIcon(getStateCount('canceledByDoctor', analyticsData), getStateCount('canceledByDoctor', analyticsDataCompare))}</td>
                               <td>{getDisplayedTotal() > 0  ? `${(getStateCount('canceledByDoctor', analyticsData)/getDisplayedTotal() * 100 ).toFixed(2)}%` : '0%'}</td>
                               <td>{getStateCount('toCall', analyticsData)} {getIcon(getStateCount('toCall', analyticsData), getStateCount('toCall', analyticsDataCompare))}</td>
                               <td>{getDisplayedTotal() > 0  ? `${(getStateCount('toCall', analyticsData)/getDisplayedTotal() * 100 ).toFixed(2)}%` : '0%'}</td>
                               <td>{getStateCount('notOnline', analyticsData)} {getIcon(getStateCount('notOnline', analyticsData), getStateCount('notOnline', analyticsDataCompare))}</td>
                               <td>{getDisplayedTotal() > 0  ? `${(getStateCount('notOnline', analyticsData)/getDisplayedTotal() * 100 ).toFixed(2)}%` : '0%'}</td>
                            </tr>

                            <tr className='compare'>
                               <td>{getStateCount('incomplete', analyticsDataCompare)}</td>
                               <td>{getDisplayedTotalCompare() > 0  ? `${(getStateCount('incomplete', analyticsDataCompare)/getDisplayedTotalCompare() * 100 ).toFixed(2)}%` : '0%'}</td>
                               <td>{getStateCount('waiting', analyticsDataCompare)}</td>
                               <td>{getDisplayedTotalCompare() > 0  ? `${(getStateCount('waiting', analyticsDataCompare)/getDisplayedTotalCompare() * 100 ).toFixed(2)}%` : '0%'}</td>
                               <td>{getStateCount('valide', analyticsDataCompare)}</td>
                               <td>{getDisplayedTotalCompare() > 0  ? `${(getStateCount('valide', analyticsDataCompare)/getDisplayedTotalCompare() * 100 ).toFixed(2)}%` : '0%'}</td>
                               <td>{getStateCount('refunded', analyticsDataCompare)}</td>
                               <td>{getDisplayedTotalCompare() > 0  ? `${(getStateCount('refunded', analyticsDataCompare)/getDisplayedTotalCompare() * 100 ).toFixed(2)}%` : '0%'}</td>
                               <td>{getStateCount('canceled', analyticsDataCompare)}</td>
                               <td>{getDisplayedTotalCompare() > 0  ? `${(getStateCount('canceled', analyticsDataCompare)/getDisplayedTotalCompare() * 100 ).toFixed(2)}%` : '0%'}</td>
                               <td>{getStateCount('canceledByDoctor', analyticsDataCompare)}</td>
                               <td>{getDisplayedTotalCompare() > 0  ? `${(getStateCount('canceledByDoctor', analyticsDataCompare)/getDisplayedTotalCompare() * 100 ).toFixed(2)}%` : '0%'}</td>
                               <td>{getStateCount('toCall', analyticsDataCompare)}</td>
                               <td>{getDisplayedTotalCompare() > 0  ? `${(getStateCount('toCall', analyticsDataCompare)/getDisplayedTotalCompare() * 100 ).toFixed(2)}%` : '0%'}</td>
                               <td>{getStateCount('notOnline', analyticsDataCompare)}</td>
                               <td>{getDisplayedTotalCompare() > 0  ? `${(getStateCount('notOnline', analyticsDataCompare)/getDisplayedTotalCompare() * 100 ).toFixed(2)}%` : '0%'}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className='source-table'>
                    <h1>Les incomplètes</h1>
                    <table className="table table-hover mb-0 analytic-table">

                        <tbody>
                            <tr className='data'>
                                <th>Total</th>
                                <th>{total}</th>
                                <th>100%</th>
                            </tr>
                            <tr className='data'>
                                <th>Les incomplètes</th>
                                <th>{getStateCount('incomplete', analyticsData)}</th>
                                <th>{total > 0 ? (getStateCount('incomplete', analyticsData) / total *100).toFixed(2) : 0} %</th>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className='source-table'>
                    <h1>Les incomplètes par étape</h1>
                    <table className="table table-hover mb-0 analytic-table">
                        <thead>
                            <tr>
                                {Object.keys(getStepObj()).filter(p => !isNaN(Number(p))).map(p => <th colSpan={2} key={p}>Étape {Number(p) +1}</th>)}
                            </tr>
                            <tr>
                                {Object.keys(getStepObj()).filter(p => !isNaN(Number(p))).map(p => [
                                    <th key={`n-${p}`}>Nombre</th>,
                                    <th key={`t-${p}`}>Taux</th>,
                                ])}
                            </tr>
                        </thead>
                        <tbody>
                            <tr className='data'>
                                {Object.keys(getStepObj()).filter(p => !isNaN(Number(p))).map(p => [
                                    <td key={`v-${p}`}>{getStepObj()[p]}</td>,
                                    <td key={`p-${p}`}>{getStateCount('incomplete', analyticsData) > 0 ? (getStepObj()[p] / getStateCount('incomplete', analyticsData) *100).toFixed(2): 0} %</td>
                                ] )}
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className='source-table'>
                    <h1>Par pathologie</h1>
                    <table className="table table-hover mb-0 analytic-table">
                        <thead>
                            <tr>
                                {Object.keys(getPathology()).map(p => <th colSpan={2} key={p}>{p}</th>)}
                            </tr>
                            <tr>
                                {Object.keys(getPathology()).map(p => [
                                    <th key={`n-${p}`}>Nombre</th>,
                                    <th key={`t-${p}`}>Taux</th>,
                                ])}
                            </tr>
                        </thead>
                        <tbody>
                            <tr className='data'>
                                {Object.keys(getPathology()).map(p => [
                                    <td key={`v-${p}`}>{getPathology()[p]}{getIcon(getPathology()[p], getPathologyCompare()[p])}</td>,
                                    <td key={`p-${p}`}>{getDisplayedTotal() > 0 && (getPathology()[p] / getDisplayedTotal() *100).toFixed(2)} %</td>
                                ] )}
                            </tr>
                        </tbody>
                    </table>
                </div>

                {
                    Object.keys(getDoctor()).length > 0 &&
                    <div className='source-table'>
                        <h1>Par médecin</h1>
                        <table className="table table-hover mb-0 analytic-table">
                            <thead>
                                <tr>
                                    {Object.keys(getDoctor()).map(p => <th colSpan={2} key={p}>{p}</th>)}
                                </tr>
                                <tr>
                                    {Object.keys(getDoctor()).map(p => [
                                        <th key={`n-${p}`}>Nombre</th>,
                                        <th key={`t-${p}`}>Taux</th>,
                                    ])}
                                </tr>
                            </thead>
                            <tbody>
                                <tr className='data'>
                                    {Object.keys(getDoctor()).map(p => [
                                        <td key={`v-${p}`}>{getDoctor()[p]} {getIcon(getDoctor()[p], getDoctorCompare()[p])}</td>,
                                        <td key={`p-${p}`}>{getDisplayedTotal() > 0 && (getDoctor()[p] / getDisplayedTotal() *100).toFixed(2)} %</td>
                                    ] )}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                }

                {
                    (typeState.indexOf(el => el.value === "generaliste") !== -1 || typeState.length === 0)  && motifs.length > 0 &&
                    <div className='source-table'>
                        <h1>Motifs des consultations générales</h1>
                        <table className="table table-hover mb-0 analytic-table">

                            {showMore &&
                                <tbody>
                                    {motifs && motifs.map((el, i) => <tr key={`motif-${i}`}>{el.map((m, j) =><td key={`m-${i}-${j}`}>{m}</td>)}</tr> )}
                                </tbody>
                            }
                            {!showMore &&
                                <tbody>
                                    <tr key={`m1`}>
                                        {motifs[0] && motifs[0].map((m, i) =><td key={`m1-${i}`}>{m}</td>)}
                                    </tr>
                                    <tr key={`m2`}>
                                        {motifs[1] && motifs[1].map((m, i) =><td key={`m2-${i}`}>{m}</td>)}
                                    </tr>
                                    <tr key={`m3`}>
                                        {motifs[2] && motifs[2].map((m, i) =><td key={`m3-${i}`}>{m}</td>)}
                                    </tr>
                                </tbody>
                            }
                        </table>

                        <button onClick={() => setShowMore(!showMore)}>{showMore ? 'Voir moins' : 'Voir plus'}</button>
                    </div>
                }
            </Panel.Body>
        </Panel>
        </div>
    );
};

export default RequestAnalytics;
